import { useEffect, useState, useRef } from "react";
import { PublicKey } from "@solana/web3.js";

const SOL_NATIVE = "11111111111111111111111111111111";
const TOKEN_PROGRAM_ID = new PublicKey("TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA");
const TOKEN_2022_PROGRAM_ID = new PublicKey("TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb");

/**
 * Fetch SOL / SPL balance for a Solana wallet.
 * @param {object} args
 * @param {import('@solana/web3.js').Connection|null} args.connection
 * @param {string|null} args.owner base58 wallet address
 * @param {{address:string, decimals:number}|null} args.token
 * @param {boolean} args.enabled
 */
export function useSolanaBalance({ connection, owner, token, enabled = true }) {
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled || !connection || !owner || !token) {
      setData(null);
      return;
    }
    let cancel = false;
    const fetchIt = async () => {
      try {
        setLoading(true);
        const wallet = new PublicKey(owner);
        // Native SOL
        if ((token.address || "").toLowerCase() === SOL_NATIVE.toLowerCase() || token.address === "So11111111111111111111111111111111111111112") {
          const lamports = await connection.getBalance(wallet, { commitment: "confirmed" });
          if (!cancel) setData(BigInt(lamports));
          return;
        }
        // SPL token via getParsedTokenAccountsByOwner
        const mint = new PublicKey(token.address);
        const [legacy, extension] = await Promise.all([
          connection.getParsedTokenAccountsByOwner(wallet, { mint, programId: TOKEN_PROGRAM_ID }),
          connection.getParsedTokenAccountsByOwner(wallet, { mint, programId: TOKEN_2022_PROGRAM_ID }).catch(() => ({ value: [] })),
        ]);
        const all = [...(legacy.value || []), ...(extension.value || [])];
        const total = all.reduce((sum, acc) => {
          const amt = acc.account.data.parsed?.info?.tokenAmount?.amount;
          return sum + BigInt(amt || 0);
        }, 0n);
        if (!cancel) setData(total);
      } catch (e) {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    fetchIt();
    timer.current = setInterval(fetchIt, 20_000);
    return () => {
      cancel = true;
      if (timer.current) clearInterval(timer.current);
    };
  }, [connection, owner, token?.address, enabled]);

  return { data, decimals: token?.decimals ?? 9, isLoading };
}
